import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@nzassa/ui';
import { Star, X } from 'lucide-react';
import { useSurvey } from '@/hooks/useSurvey';
import { useFocusTrap } from '@/hooks/useFocusTrap';
import { useBodyScrollLock } from '@/hooks/useBodyScrollLock';

type SurveyType =
  | 'confidence_pre'
  | 'confidence_post'
  | 'satisfaction_session'
  | 'coach_feedback'
  | 'post_session_utility'
  | 'nps_programme'
  | 'intention_post';

interface MicroSurveyModalProps {
  surveyType: SurveyType;
  dismissible?: boolean;
  onClose: () => void;
}

export function MicroSurveyModal({ surveyType, dismissible = true, onClose }: MicroSurveyModalProps) {
  const { t } = useTranslation();
  const { submit, loading, submitted } = useSurvey(surveyType);
  const [value, setValue] = useState<unknown>(null);
  const [textValue, setTextValue] = useState('');

  const SURVEY_TITLES: Record<SurveyType, string> = {
    confidence_pre: t('survey.confidence_pre'),
    confidence_post: t('survey.confidence_post'),
    satisfaction_session: t('survey.satisfaction_session'),
    coach_feedback: t('survey.coach_feedback'),
    post_session_utility: t('survey.post_session_utility'),
    nps_programme: t('survey.nps_programme'),
    intention_post: t('survey.intention_post'),
  };

  const handleSubmit = async () => {
    if (value === null && !textValue) return;
    await submit(value, textValue || undefined);
    onClose();
  };

  const handleEscape = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape' && dismissible) onClose();
  }, [dismissible, onClose]);

  useEffect(() => {
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [handleEscape]);

  const trapRef = useFocusTrap<HTMLDivElement>(!submitted);
  useBodyScrollLock(!submitted);

  if (submitted) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={dismissible ? onClose : undefined} role="dialog" aria-modal="true" aria-label={SURVEY_TITLES[surveyType]}>
      <div
        ref={trapRef}
        className="bg-white rounded-2xl shadow-2xl p-6 mx-4 w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-slate-800">
            {SURVEY_TITLES[surveyType]}
          </h3>
          {dismissible && (
            <button onClick={onClose} aria-label={t('common.fermer')} className="text-slate-400 hover:text-slate-600">
              <X size={20} />
            </button>
          )}
        </div>

        {/* Content by type */}
        {(surveyType === 'confidence_pre' || surveyType === 'confidence_post') && (
          <StarRating value={value as number} onChange={setValue} />
        )}

        {surveyType === 'satisfaction_session' && (
          <EmojiRating value={value as number} onChange={setValue} />
        )}

        {surveyType === 'coach_feedback' && (
          <CoachFeedback
            value={value as boolean | null}
            onChange={setValue}
            comment={textValue}
            onCommentChange={setTextValue}
          />
        )}

        {surveyType === 'post_session_utility' && (
          <UtilityChoice value={value as string | null} onChange={setValue} />
        )}

        {surveyType === 'nps_programme' && (
          <NpsScale
            value={value as number | null}
            onChange={setValue}
            comment={textValue}
            onCommentChange={setTextValue}
          />
        )}

        {surveyType === 'intention_post' && (
          <IntentionText value={textValue} onChange={setTextValue} onSetValue={setValue} />
        )}

        {/* Submit */}
        <Button
          className="w-full mt-4"
          onClick={handleSubmit}
          disabled={loading || (value === null && !textValue)}
        >
          {loading ? t('survey.envoi') : t('survey.valider')}
        </Button>
      </div>
    </div>
  );
}

/* Sub-components */

function StarRating({ value, onChange }: { value: number | null; onChange: (v: number) => void }) {
  return (
    <div className="flex justify-center gap-2 py-4">
      {[1, 2, 3, 4, 5].map((n) => (
        <button key={n} onClick={() => onChange(n)} className="transition-transform hover:scale-110">
          <Star
            size={36}
            className={n <= (value ?? 0) ? 'fill-yellow-400 text-yellow-400' : 'text-slate-300'}
          />
        </button>
      ))}
    </div>
  );
}

function EmojiRating({ value, onChange }: { value: number | null; onChange: (v: number) => void }) {
  const emojis = [
    { label: '\ud83e\udd29', value: 3 },
    { label: '\ud83d\udc4d', value: 2 },
    { label: '\ud83d\ude13', value: 1 },
  ];
  return (
    <div className="flex justify-center gap-6 py-4">
      {emojis.map((e) => (
        <button
          key={e.value}
          onClick={() => onChange(e.value)}
          className={`text-4xl transition-transform hover:scale-125 ${
            value === e.value ? 'scale-125 ring-2 ring-blue-400 rounded-full p-1' : ''
          }`}
        >
          {e.label}
        </button>
      ))}
    </div>
  );
}

function CoachFeedback({
  value,
  onChange,
  comment,
  onCommentChange,
}: {
  value: boolean | null;
  onChange: (v: boolean) => void;
  comment: string;
  onCommentChange: (v: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="space-y-3 py-2">
      <div className="flex justify-center gap-4">
        {[
          { label: `${t('survey.oui')} \u2705`, val: true },
          { label: `${t('survey.non')} \u274c`, val: false },
        ].map((opt) => (
          <button
            key={String(opt.val)}
            onClick={() => onChange(opt.val)}
            className={`px-6 py-2 rounded-xl border-2 text-sm font-medium transition ${
              value === opt.val
                ? 'border-blue-500 bg-blue-50 text-blue-700'
                : 'border-slate-200 hover:border-slate-300'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>
      <textarea
        placeholder={t('survey.commentaire_optionnel')}
        value={comment}
        onChange={(e) => onCommentChange(e.target.value)}
        className="w-full border rounded-xl p-3 text-sm resize-none h-20"
      />
    </div>
  );
}

function UtilityChoice({
  value,
  onChange,
}: {
  value: string | null;
  onChange: (v: string) => void;
}) {
  const { t } = useTranslation();
  const options = [
    { label: `\ud83c\udfa5 ${t('survey.videos')}`, value: 'video' },
    { label: `\ud83d\udcdd ${t('survey.quiz')}`, value: 'quiz' },
    { label: `\ud83d\udee0\ufe0f ${t('survey.ateliers')}`, value: 'atelier' },
    { label: `\ud83e\udd16 ${t('survey.coach_ia')}`, value: 'coach' },
  ];
  return (
    <div className="grid grid-cols-2 gap-2 py-2">
      {options.map((opt) => (
        <button
          key={opt.value}
          onClick={() => onChange(opt.value)}
          className={`px-4 py-3 rounded-xl border-2 text-sm font-medium transition ${
            value === opt.value
              ? 'border-blue-500 bg-blue-50 text-blue-700'
              : 'border-slate-200 hover:border-slate-300'
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

function NpsScale({
  value,
  onChange,
  comment,
  onCommentChange,
}: {
  value: number | null;
  onChange: (v: number) => void;
  comment: string;
  onCommentChange: (v: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="space-y-3 py-2">
      <div className="flex justify-between gap-1">
        {Array.from({ length: 11 }, (_, i) => (
          <button
            key={i}
            onClick={() => onChange(i)}
            className={`w-8 h-8 rounded-lg text-xs font-bold transition ${
              value === i
                ? 'bg-blue-600 text-white'
                : i <= 6
                  ? 'bg-red-50 text-red-600 hover:bg-red-100'
                  : i <= 8
                    ? 'bg-yellow-50 text-yellow-600 hover:bg-yellow-100'
                    : 'bg-green-50 text-green-600 hover:bg-green-100'
            }`}
          >
            {i}
          </button>
        ))}
      </div>
      <div className="flex justify-between text-xs text-slate-400">
        <span>{t('survey.pas_du_tout')}</span>
        <span>{t('survey.absolument')}</span>
      </div>
      <textarea
        placeholder={t('survey.pourquoi')}
        value={comment}
        onChange={(e) => onCommentChange(e.target.value)}
        className="w-full border rounded-xl p-3 text-sm resize-none h-20"
      />
    </div>
  );
}

function IntentionText({
  value,
  onChange,
  onSetValue,
}: {
  value: string;
  onChange: (v: string) => void;
  onSetValue: (v: unknown) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="py-2">
      <textarea
        placeholder={t('survey.decris')}
        value={value}
        onChange={(e) => {
          const text = e.target.value.slice(0, 200);
          onChange(text);
          onSetValue(text);
        }}
        className="w-full border rounded-xl p-3 text-sm resize-none h-24"
        maxLength={200}
      />
      <p className="text-right text-xs text-slate-400 mt-1">{value.length}/200</p>
    </div>
  );
}
